import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { useGetAssignedTeams } from "api/queries/quotation/useGetAssignedTeams";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import Button from "components/v2/Button/Button";
import Card from "components/v2/Card/Card";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import NoQuotesSubmitted from "pages/common/ShipmentQuotesV2/components/NoQuotesSubmitted";
import QuotationList from "pages/common/ShipmentQuotesV2/components/QuotationList";
import QuotationBanners from "pages/common/ShipmentQuotesV2/components/QuotationBanners/QuotationBanners";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

function QuotesTable() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams();
  const shipmentId = id!;

  const { data: quotationData, isLoading: isLoadingQuotationsData } = useGetQuotations({
    shipmentId,
    options: {
      enabled: !!shipmentId,
    },
  });
  const quotations = quotationData?.quotations || [];

  const {
    data: quoteDetailsData,
    isLoading,
    isInitialLoading: loadingData,
  } = useGetQuoteRequestDetails({
    shipmentId,
    options: {
      enabled: !!shipmentId,
      refetchOnMount: false,
    },
  });

  const { data: shipmentData } = useGetShipmentBasic(shipmentId, {
    enabled: !!shipmentId,
  });

  const { data: assignedTeams = [] } = useGetAssignedTeams({
    id: id!,
    config: { params: { already_assigned: true } },
  });

  const { request_status, quoting_deadline_seconds_remaining } = quoteDetailsData?.organization || {};
  const isQuotingDeadlineExpired = quoting_deadline_seconds_remaining <= 0;
  const isAssigned = assignedTeams.length > 0;

  const isShipmentBooked = SHIPMENT_STATUS.includes(shipmentData?.shipment?.request_status);

  const showSubmitQuoteButton =
    !isQuotingDeadlineExpired && !["Uninvited", "Declined"].includes(request_status) && isAssigned && !isShipmentBooked;
  const showSubmitFirstQuotationButton =
    !isQuotingDeadlineExpired &&
    quotations?.length === 0 &&
    !isLoadingQuotationsData &&
    !loadingData &&
    showSubmitQuoteButton;

  const handleClickSubmitQuote = () => {
    navigate(`/shipment/${id}/quotation/add`);
  };

  return (
    <div>
      <Card borderRadius="md">
        <Card.Header
          title={intl.formatMessage({
            id: "general.quotes",
            defaultMessage: "Quotes",
          })}
          showBorder
          actions={
            showSubmitQuoteButton ? (
              <div className="d-flex align-items-center h-100">
                <Button
                  label={intl.formatMessage({
                    id: "quote.submitAQuote",
                    defaultMessage: "Submit a quote",
                  })}
                  onClick={handleClickSubmitQuote}
                  iconLeft={<PlusIcon />}
                  variant="link-color"
                />
              </div>
            ) : undefined
          }
        />
        {!isLoading && <QuotationBanners />}
        <div className="px-3xl">
          {isLoading ? (
            <LoadingSkeleton rows={1} />
          ) : quotations.length === 0 ? (
            <div className="py-3xl">
              <NoQuotesSubmitted>
                <p className="mt-xs text-tertiary">
                  <FormattedMessage
                    id="quote.noQuoteSubmitted.provider.subtitle"
                    defaultMessage="Submit a quote to bid for a shipment"
                  />
                </p>
                {showSubmitFirstQuotationButton && (
                  <Button
                    label={intl.formatMessage({
                      id: "quote.submitAQuote",
                      defaultMessage: "Submit a quote",
                    })}
                    onClick={handleClickSubmitQuote}
                    iconLeft={<PlusIcon />}
                  />
                )}
              </NoQuotesSubmitted>
            </div>
          ) : (
            <QuotationList quotations={quotations} />
          )}
        </div>
      </Card>
    </div>
  );
}

export default QuotesTable;
